import type { JSX } from 'react';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from '#src/shared/prefetch';
import { withPrefetchedData } from '#src/shared/prefetch';

import type {
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from '#src/app/pages/petitionDetails/pageContext';
import { prefetchPageContext, prefetchPageUserContext } from '#src/app/pages/petitionDetails/pageContext';
import type { Sharer } from '#src/app/pages/petitionDetails/shared/types';
import { headlineReplacements } from '#src/app/pages/petitionDetails/shared/utils';
import { isLoaded } from '#src/app/shared/utils/async';

import { getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

import type { ReadyToSharePreviewLinkDetails } from './api/getPetitionReadyToSharePreviewLink';
import { getPetitionReadyToSharePreviewLink } from './api/getPetitionReadyToSharePreviewLink';

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		metaContext: PetitionDetailsMetaContext;
		sharer?: Sharer;
		previewLink?: ReadyToSharePreviewLinkDetails | null;
		shareChannel: string;
	}>;

function getShareForStartersTitle(
	i18n: UtilityContext['i18n'],
	channel: string,
	replacements: Record<string, string | number>,
): string {
	switch (channel) {
		case 'facebook':
			return i18n.translate('fe.components.petition_share.share_text_fb', replacements);
		case 'whatsapp':
			return i18n.translate('corgi.share_for_starters.share_text.whatsapp.title', replacements);
		case 'copy':
			return i18n.translate('corgi.share_for_starters.share_text.copylink.title', replacements);
		default:
			return i18n.translate('fe.components.petition_share.share_text', replacements);
	}
}

function getShareForStartersDescription(
	i18n: UtilityContext['i18n'],
	channel: string,
	replacements: Record<string, string | number>,
): string {
	switch (channel) {
		case 'facebook':
			return i18n.translate('corgi.share_for_starters.share_text.facebook.description', replacements);
		default:
			return replacements.petition_name as string;
	}
}

export const PetitionDetailsShareForStarters = withPrefetchedData(
	({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> &
		PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>): JSX.Element => {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { sharerUserId: sharerUserIdParam, slug: slugParam, previewId, shareChannel: shareChannelParam },
			} = context;

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const slug = slugParam!;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const sharerUserId = sharerUserIdParam!;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const shareChannel = shareChannelParam!;

			const [pageContext, previewLink, sharer] = await Promise.all([
				prefetchPageContext(slug, context),
				previewId
					? getPetitionReadyToSharePreviewLink(slug, context.utilityContext, previewId).catch(() => null)
					: undefined,
				getSharer(sharerUserId, context.utilityContext).catch(() => undefined),
			]);

			return {
				...pageContext,
				metaContext: buildMetaContext(context),
				previewLink,
				sharer,
				shareChannel,
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const shareHeadlineOverrides = (() => {
				if (!prefetchedData.sharer) {
					return undefined;
				}
				const { previewLink } = prefetchedData;
				if (previewLink) {
					const [firstImage] = previewLink.images;
					return {
						title: previewLink.title,
						description: '',
						imageUrl: firstImage?.url,
					};
				}
				const channel = prefetchedData.shareChannel;
				const replacements = headlineReplacements({
					pageData: prefetchedData.pageData.pageData,
					sharer: prefetchedData.sharer,
					i18n,
				});
				return {
					title: getShareForStartersTitle(i18n, channel, replacements),
					description: getShareForStartersDescription(i18n, channel, replacements),
				};
			})();
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				shareHeadlineOverrides,
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
